export default ` 
  **Dernière mise à jour : 28 août 2025**
  
  Nurau Inc. («Nurau», «nous», «notre» ou «nos») s'engage à protéger la vie privée et les renseignements personnels des personnes utilisant l'application mobile Nurau (l'«App»). La présente Politique de confidentialité explique comment nous recueillons, utilisons, divulguons, conservons et protégeons les renseignements personnels conformément à:
  - la Loi 25 du Québec visant à moderniser certaines dispositions législatives en matière de protection des renseignements personnels,
  - la Loi canadienne sur la protection des renseignements personnels et les documents électroniques (LPRPDE) et aux lois provinciales applicables, et
  - le Règlement général sur la protection des données (RGPD) de l'Union européenne, le cas échéant.
  **En utilisant l'App, vous reconnaissez et consentez aux pratiques décrites dans la présente Politique de confidentialité, sous réserve de vos droits prévus par les lois applicables.**
  
#### 1. Responsabilité et gouvernance
  **Responsable de la protection des renseignements personnels**
  Nurau Inc. a désigné un Responsable de la protection des renseignements personnels chargé du respect des lois sur la vie privée, y compris la Loi 25.
  
  **Coordonnées**  
  Me Ava Chisling, B.A., B.C.L., LL.B.  
  Avocate  
  Nurau Inc.  
  3978 St-André, Montréal, Québec H2L 3W1  
  Courriel: legal@nurau.com  
  
  Nous répondrons à toutes les demandes relatives à la vie privée rapidement, conformément aux lois applicables.
  
#### 2. Portée
  La présente Politique de confidentialité s'applique aux renseignements personnels recueillis, utilisés, divulgués et conservés par l'intermédiaire de l'App Nurau. Elle ne s'applique pas aux sites Web externes ni aux applications tierces.
  
#### 3. Renseignements que nous recueillons
  Nous recueillons uniquement les renseignements personnels nécessaires au fonctionnement et à l'amélioration de l'App, conformément au principe de minimisation des données.
  
  **a. Catégories de renseignements recueillis:**
  - Renseignements relatifs au compte: nom, adresse courriel, nom de l'organisation et identifiants de connexion.
  - Entrées relatives aux politiques de l'entreprise: contenu téléchargé ou consulté par les gestionnaires, limité aux politiques organisationnelles (non aux dossiers personnels des employés).
  - Données d'utilisation: type d'appareil, système d'exploitation, version de l'application, adresse IP et activité d'utilisation.
  - Renseignements de support: données fournies lors d'une demande de support.
  - Renseignements sensibles: l'App n'est pas conçue pour recueillir des données personnelles sensibles (ex.: santé, biométrie, numéros d'assurance sociale).
  
  **b. Avis au moment de la collecte:**
  Au moment de la collecte, vous serez informé de:
  1. Les finalités pour lesquelles les renseignements sont recueillis.
  2. Les moyens par lesquels ils sont recueillis.
  3. Vos droits d'accès, de rectification et de retrait du consentement.
  4. La possibilité que les renseignements soient divulgués hors Québec, le cas échéant.
  
#### 4. Finalités de la collecte
  Nous utilisons les renseignements personnels strictement aux fins suivantes:
  - Fournir et exploiter l'App.
  - Générer des réponses assistées par IA aux questions en milieu de travail, sur la base des politiques de l'entreprise.
  - Améliorer la fonctionnalité, la sécurité et l'expérience utilisateur.
  - Respecter les obligations légales.
  - Communiquer les mises à jour essentielles.
  
  **Nurau agit comme un outil passif: l'App ne remplace pas les décisions managériales ou syndicales. Une validation humaine est toujours requise.**
  
#### 5. Consentement
  - Le consentement est explicite, libre, éclairé et spécifique.
  - Le consentement est recueilli séparément des autres conditions (ex.: Conditions d'utilisation).
  - Les utilisateurs peuvent retirer leur consentement à tout moment, sous réserve des restrictions légales ou contractuelles.
  - Cette application n'est pas destinée aux mineurs et ne doit pas être utilisée par eux.
  
#### 6. Base légale du traitement (RGPD)
  Lorsque le RGPD s'applique, le traitement repose sur:
  - Le consentement (pour les fonctionnalités optionnelles).
  - La nécessité contractuelle (pour fournir les services).
  - L'intérêt légitime (amélioration de la sécurité et des fonctionnalités).
  - Les obligations légales (conformité aux lois québécoises et canadiennes).
  
#### 7. Utilisation et divulgation des renseignements
  Les renseignements personnels ne peuvent être divulgués:
  - Qu'avec votre consentement exprès.
  - À des fournisseurs de services soumis à des obligations contractuelles de conformité à la Loi 25 et à la LPRPDE.
  - Selon les exigences légales, assignation à comparaître ou enquête.
  - Dans le cadre d'une opération commerciale (ex.: fusion, vente d'actifs), avec des mesures de protection appropriées.
  - Sous forme agrégée ou anonymisée à des fins statistiques.
  Nous ne vendons ni ne louons les renseignements personnels.
  Avertissement sur les données des employés
  L'App est conçue pour traiter les politiques organisationnelles et les directives générales en milieu de travail. Elle n'est pas destinée à traiter les dossiers individuels des employés. Toute divulgation de renseignements personnels d'employés doit être légale et autorisée par l'employeur.
  
#### 8. Cookies et technologies de suivi
  L'App peut utiliser des cookies ou technologies équivalentes telles que des SDK mobiles:
  - Essentiels: nécessaires pour la sécurité, l'authentification ou les fonctionnalités de base.
  - Analyse/Performance: pour comprendre l'utilisation de l'App et améliorer les services.
  - Suivi non essentiel: désactivé par défaut et nécessitant votre consentement exprès.
  Vous pouvez gérer ou retirer votre consentement via les paramètres de votre appareil.
  
#### 9. Transferts transfrontaliers
  Lorsque des renseignements personnels sont conservés ou traités hors Québec:
  - Nurau effectuera et documentera une Analyse d'impact sur la vie privée (AIVP).
  - Les transferts n'auront lieu que si la juridiction offre une protection adéquate ou avec des garanties contractuelles.
  
#### 10. Conservation et destruction
  Les renseignements personnels sont conservés uniquement aussi longtemps que nécessaire aux fins identifiées, ou conformément à la loi.
  
  **Calendrier de conservation:**
  - Données de compte: 2 mois après la fermeture du compte.
  - Journaux d'utilisation: jusqu'à 12 mois.
  - Données de support: jusqu'à 24 mois.
  - Sauvegardes: 90 jours.
  
  Après la période de conservation, les données seront détruites de manière sécurisée ou anonymisées.
  
#### 11. Prise de décision automatisée et utilisation de l'IA
  - L'App utilise l'IA pour générer des réponses suggérées.
  - L'IA est passive et ne prend pas de décisions affectant les employés de manière autonome.
  - Toutes les sorties nécessitent une validation humaine.
  - Aucun profilage ni aucune décision automatisée ayant des effets juridiques ou significatifs n'est effectué sans votre consentement exprès.
  
#### 12. Droits des utilisateurs
  Vous disposez des droits suivants en vertu de la Loi 25, de la LPRPDE et du RGPD:
  - Accès: obtenir une copie de vos renseignements personnels.
  - Rectification: corriger les informations inexactes ou incomplètes.
  - Suppression: demander l'effacement de vos renseignements personnels.
  - Portabilité: recevoir vos données dans un format structuré et lisible par machine.
  - Limitation/Objection: limiter ou s'opposer au traitement dans certains cas.
  - Désindexation/Reindexation: demander la suppression ou la correction des informations publiques.
  - Retrait du consentement: à tout moment, sous réserve des obligations légales.
  Les demandes doivent être envoyées à legal@nurau.com. Nous vérifierons votre identité et répondrons dans un délai de 30 jours.
  
#### 13. Mesures de sécurité
  Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger les renseignements personnels, notamment:
  • Chiffrement en transit et au repos.
  • Restrictions d'accès et contrôles basés sur les rôles.
  • Journalisation et surveillance des activités du système.
  • Procédures de destruction sécurisée.
  
#### 14. Incidents de confidentialité et notification de violation
  En cas d'incident de confidentialité (accès, utilisation ou divulgation non autorisés):
  • Nous évaluerons le risque de préjudice sérieux.
  • Le cas échéant, nous informerons les personnes concernées et la Commission d'accès à l'information du Québec (CAI) sans délai.
  • Nous tenons un registre interne de tous les incidents de confidentialité, accessible à la CAI sur demande.
  
#### 15. Protection de la vie privée dès la conception et par défaut
  L'App est conçue de manière à:
  • Recueillir uniquement les renseignements personnels strictement nécessaires à chaque finalité.
  • Définir les paramètres de confidentialité au niveau le plus élevé par défaut.
  • Intégrer la protection de la vie privée dans les choix de conception.
  
#### 16. Vie privée des enfants
  L'App n'est pas destinée aux enfants et ne doit pas être utilisée par eux.
  
#### 17. Modifications de la politique
  Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. Toute modification importante sera communiquée via des avis dans l'App ou par courriel. L'utilisation continue de l'App après les mises à jour constitue une acceptation.
  
#### 18. Droit applicable et juridiction
  La présente Politique de confidentialité et tout litige y afférent sont régis par les lois du Québec et les lois canadiennes applicables. Tout litige sera soumis à la compétence exclusive des tribunaux de Montréal, Québec.
  
#### 19. Contact  
  Nurau Inc.  
  3978 St-André  
  Montréal, Québec H2L 3W1  
  Canada  
  Courriel: legal@nurau.com
`;
